import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronLeft, Check, Menu, ScanLine, Search, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandGroup, CommandItem, CommandList } from '../ui/command';
import { cn } from '@/lib/utils';
import Breadcrumb, { BreadcrumbItem } from '../shared/Breadcrumb';

export interface SearchField {
    id: string;
    label: string;
    placeholder: string;
}

const DEFAULT_SEARCH_FIELD: SearchField = {
    id: 'default',
    label: 'ค้นหา',
    placeholder: 'ค้นหา…',
};

interface PageHeaderProps {
    title?: string;
    breadcrumbItems?: BreadcrumbItem[];
    showBackButton?: boolean;
    onBack?: () => void;
    enableSearch?: boolean;
    searchFields?: SearchField[];
    onSearch?: (query: string, field: string) => void;
    onSearchFieldChange?: (field: string) => void;
    initialSearchQuery?: string;
    initialSearchField?: string;
    enableScan?: boolean;
    onMenuClick?: () => void;
    primaryAction?: {
        label: string;
        onClick: () => void;
        variant?: 'primary' | 'secondary';
    };
    children?: ReactNode;
}

/**
 * The page's own header, rendered inside the content and scrolling with it.
 *
 * This replaces the old fixed topbar. Anything that belonged to the app rather than
 * to the page — the account menu, notifications, the sidebar toggle — lives in the
 * sidebar now, so nothing here is duplicated chrome.
 */
const PageHeader: React.FC<PageHeaderProps> = ({
    title,
    breadcrumbItems = [],
    showBackButton = false,
    onBack,
    enableSearch = false,
    searchFields = [],
    onSearch,
    onSearchFieldChange,
    initialSearchQuery = '',
    initialSearchField,
    enableScan = false,
    onMenuClick,
    primaryAction,
    children,
}) => {
    const navigate = useNavigate();
    const [isFieldPickerOpen, setIsFieldPickerOpen] = useState(false);
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
    const [isStuck, setIsStuck] = useState(false);
    const sentinelRef = useRef<HTMLDivElement>(null);

    // The header is pinned to the top of the scroll area. A zero-height sentinel just
    // above it tells us when the page has scrolled under it, so the separating line
    // only appears once there is something to separate — the way a native app's bar
    // gains its hairline on scroll.
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || typeof IntersectionObserver === 'undefined') return;
        const observer = new IntersectionObserver(
            ([entry]) => setIsStuck(!entry.isIntersecting),
            { threshold: 1 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, []);
    const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
    const [pickedFieldId, setPickedFieldId] = useState<string | undefined>(initialSearchField);
    // Resolve on every render so a page swapping its search fields can never leave a stale one selected.
    const selectedField: SearchField =
        searchFields.find(f => f.id === pickedFieldId) ?? searchFields[0] ?? DEFAULT_SEARCH_FIELD;


    const handleFieldSelect = (field: SearchField) => {
        setPickedFieldId(field.id);
        setIsFieldPickerOpen(false);
        onSearchFieldChange?.(field.id);
        onSearch?.(searchQuery, field.id);
    };

    const handleSearchChange = (query: string) => {
        setSearchQuery(query);
        onSearch?.(query, selectedField.id);
    };

    const showSearch = enableSearch && searchFields.length > 0;
    const hasLeading = showBackButton || breadcrumbItems.length > 0 || !!title || !!onMenuClick;
    const hasTitleBlock = !!title || breadcrumbItems.length > 0;
    const leadingGrows = hasTitleBlock || !children;
    // Below md the leading group still carries the drawer button, but above it the
    // group can be completely empty — and an empty flex child still costs the row's
    // gap, which left such a page's header 12px out of line with every other one.
    const hideLeadingOnDesktop = !hasTitleBlock && !showBackButton && !!children;
    const hasTrailing = showSearch || enableScan || !!primaryAction || !!children;

    if (!hasLeading && !hasTrailing) return null;

    return (
        <>
        <div ref={sentinelRef} aria-hidden="true" className="h-px" />
        <div
            // z-35 sits above page content (card actions z-20, filter dropdowns z-30)
            // and below the mobile drawer (scrim z-40, panel z-50).
            className={`sticky top-0 z-[35] bg-white px-3 transition-shadow duration-150 sm:px-6 ${
                hasTrailing ? 'pb-3 pt-2.5 sm:pt-5' : 'pb-2 pt-2 sm:pb-2.5 sm:pt-3'
            } border-b border-[#E6EAEE] ${
                isStuck ? 'shadow-[0_1px_2px_rgba(14,20,25,0.05)]' : ''
            }`}
            style={{ top: 'env(safe-area-inset-top, 0px)' }}
        >
            {/* Phone search mode: the row becomes the field, so the header never
                grows a second line just to hold a search box. */}
            {showSearch && isMobileSearchOpen && (
                <div className="flex items-center gap-2 sm:hidden">
                    <button
                        type="button"
                        onClick={() => setIsMobileSearchOpen(false)}
                        className="-ml-2.5 flex h-11 w-11 flex-none items-center justify-center rounded-lg text-[#0E1419] transition-colors hover:bg-[#F2F5F7]"
                        aria-label="ปิดค้นหา"
                    >
                        <ChevronLeft className="h-5 w-5" strokeWidth={1.75} />
                    </button>

                    <div className="relative flex h-11 min-w-0 flex-1 items-center rounded-lg border border-[#C3CBEE] bg-white">
                        <Search className="pointer-events-none absolute left-3 text-[#8892A0]" size={15} />
                        <input
                            type="text"
                            autoFocus
                            value={searchQuery}
                            placeholder={selectedField.placeholder}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            className="h-full w-full min-w-0 bg-transparent pl-9 pr-8 text-[13.5px] text-[#0E1419] placeholder:text-[#8892A0] focus:outline-none"
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={() => handleSearchChange('')}
                                className="absolute right-2.5 text-[#8892A0]"
                                aria-label="ล้างคำค้นหา"
                            >
                                <X size={15} />
                            </button>
                        )}
                    </div>

                    {searchFields.length > 1 && (
                        <Popover open={isFieldPickerOpen} onOpenChange={setIsFieldPickerOpen}>
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    className="flex h-11 flex-none items-center gap-1 rounded-lg border border-[#DDE2E7] px-3 text-[12.5px] text-[#4E5964]"
                                    aria-label="เลือกช่องค้นหา"
                                >
                                    {selectedField.label}
                                    <ChevronDown size={13} className="text-[#8892A0]" />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-40 p-0" align="end">
                                <Command>
                                    <CommandList>
                                        <CommandGroup>
                                            {searchFields.map((field) => (
                                                <CommandItem key={field.id} value={field.label} onSelect={() => handleFieldSelect(field)} className="flex items-center gap-2">
                                                    <Check className={cn('h-4 w-4', selectedField.id === field.id ? 'text-[#1B4B8F] opacity-100' : 'opacity-0')} />
                                                    {field.label}
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    )}
                </div>
            )}

            <div className={`flex-wrap items-center gap-x-1.5 gap-y-2 sm:flex sm:gap-x-3 ${showSearch && isMobileSearchOpen ? 'hidden' : 'flex'}`}>
                {/* The leading group only grows when it has something to show. It used
                    to take flex-1 unconditionally, so a page with no title — the board
                    supplies its own heading through customHeader — got an empty
                    spacer on desktop (the menu button inside is md:hidden) that pushed
                    its header content to the right edge, out of line with every other
                    page. When nothing else can absorb the space it still grows, so
                    trailing actions stay right. */}
                <div className={`flex min-w-0 items-center gap-2 ${leadingGrows ? 'flex-1' : 'flex-none'} ${hideLeadingOnDesktop ? 'md:hidden' : ''}`}>
                    {onMenuClick && (
                        <button
                            type="button"
                            onClick={onMenuClick}
                            className="-ml-2.5 flex h-11 w-11 flex-none items-center justify-center rounded-lg text-[#0E1419] transition-colors hover:bg-[#F2F5F7] md:hidden"
                            aria-label="เปิดเมนู"
                        >
                            <Menu className="h-5 w-5" strokeWidth={1.75} />
                        </button>
                    )}

                    {showBackButton && (
                        <button
                            type="button"
                            onClick={onBack ?? (() => navigate(-1))}
                            className="flex h-11 w-11 flex-none items-center justify-center rounded-lg border border-[#DDE2E7] text-[#4E5964] transition-colors hover:bg-[#F2F5F7] sm:h-8 sm:w-8"
                            aria-label="ย้อนกลับ"
                        >
                            <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
                        </button>
                    )}

                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        {breadcrumbItems.length > 0 && (
                            <Breadcrumb items={breadcrumbItems} showBackButton={false} />
                        )}
                        {title && (
                            <h1 className="truncate text-[15px] font-medium tracking-[-0.01em] text-[#0E1419] sm:text-lg">
                                {title}
                            </h1>
                        )}
                    </div>
                </div>

                {children && (
                    <div className={`flex min-w-0 items-center gap-2 ${hasTitleBlock ? '' : 'flex-1'}`}>
                        {children}
                    </div>
                )}

                {/* Phone: search collapses to an icon that opens the row above. */}
                {showSearch && (
                    <button
                        type="button"
                        onClick={() => setIsMobileSearchOpen(true)}
                        className={`flex h-11 w-11 flex-none items-center justify-center rounded-lg border transition-colors sm:hidden ${
                            searchQuery ? 'border-[#C3CBEE] bg-[#EEF2FB] text-[#1B4B8F]' : 'border-[#DDE2E7] bg-white text-[#4E5964]'
                        }`}
                        aria-label="ค้นหา"
                    >
                        <Search className="h-4 w-4" strokeWidth={1.75} />
                    </button>
                )}

                {enableScan && (
                    <button
                        type="button"
                        onClick={() => navigate('/scan')}
                        className="flex h-11 w-11 flex-none items-center justify-center rounded-lg border border-[#DDE2E7] bg-white text-[#4E5964] transition-colors hover:bg-[#F2F5F7] sm:hidden"
                        aria-label="สแกน"
                    >
                        <ScanLine className="h-4 w-4" strokeWidth={1.75} />
                    </button>
                )}

                {/* Desktop: the finder stays one shell with scan attached. */}
                {showSearch && (
                    <div className="hidden h-8 min-w-0 items-stretch overflow-hidden rounded-lg border border-[#DDE2E7] bg-white sm:flex">
                        <Popover open={isFieldPickerOpen} onOpenChange={setIsFieldPickerOpen}>
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    role="combobox"
                                    aria-expanded={isFieldPickerOpen}
                                    className="flex items-center gap-1.5 whitespace-nowrap border-r border-[#DDE2E7] bg-[#FAFBFC] px-2.5 text-[12.5px] text-[#4E5964] transition-colors hover:bg-[#F2F5F7]"
                                >
                                    {selectedField.label}
                                    <ChevronDown size={13} className={`text-[#8892A0] transition-transform duration-150 ${isFieldPickerOpen ? 'rotate-180' : ''}`} />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-40 p-0" align="start">
                                <Command>
                                    <CommandList>
                                        <CommandGroup>
                                            {searchFields.map((field) => (
                                                <CommandItem key={field.id} value={field.label} onSelect={() => handleFieldSelect(field)} className="flex items-center gap-2">
                                                    <Check className={cn('h-4 w-4', selectedField.id === field.id ? 'text-[#1B4B8F] opacity-100' : 'opacity-0')} />
                                                    {field.label}
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>

                        <div className="relative flex min-w-0 items-center">
                            <Search className="pointer-events-none absolute left-2.5 text-[#8892A0]" size={14} />
                            <input
                                type="text"
                                value={searchQuery}
                                placeholder={selectedField.placeholder}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                // A fixed width here made the search the one thing that
                                // never gave way, so the page title truncated instead —
                                // "ทะเบียนแม่พิมพ์" became "ทะเบียนแม่พิ…" on a tablet.
                                className="h-full w-[140px] bg-transparent pl-8 pr-7 text-[12.5px] text-[#0E1419] placeholder:text-[#8892A0] focus:outline-none md:w-[180px] lg:w-[220px]"
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => handleSearchChange('')}
                                    className="absolute right-2 text-[#8892A0] transition-colors hover:text-[#0E1419]"
                                    aria-label="ล้างคำค้นหา"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>

                        {enableScan && (
                            <button
                                type="button"
                                onClick={() => navigate('/scan')}
                                className="flex flex-none items-center gap-2 border-l border-[#DDE2E7] bg-[#FAFBFC] px-3 text-[12.5px] text-[#0E1419] transition-colors hover:bg-[#F2F5F7]"
                            >
                                <ScanLine className="h-3.5 w-3.5 text-[#4E5964]" strokeWidth={1.75} />
                                สแกน
                            </button>
                        )}
                    </div>
                )}

                {enableScan && !showSearch && (
                    <button
                        type="button"
                        onClick={() => navigate('/scan')}
                        className="hidden h-8 flex-none items-center gap-2 rounded-lg border border-[#DDE2E7] bg-white px-3 text-[12.5px] text-[#0E1419] transition-colors hover:bg-[#F2F5F7] sm:flex"
                    >
                        <ScanLine className="h-3.5 w-3.5 text-[#4E5964]" strokeWidth={1.75} />
                        สแกน
                    </button>
                )}

                {primaryAction && (
                    <button
                        type="button"
                        onClick={primaryAction.onClick}
                        className={`flex h-11 flex-none items-center whitespace-nowrap rounded-lg px-3 text-[13px] font-semibold transition-colors sm:ml-1 sm:h-8 sm:px-4 sm:text-[12.5px] ${
                            primaryAction.variant === 'secondary'
                                ? 'border border-[#DDE2E7] bg-white text-[#0E1419] hover:bg-[#F2F5F7]'
                                : 'bg-[#1B4B8F] text-white hover:bg-[#123464]'
                        }`}
                    >
                        {primaryAction.label}
                    </button>
                )}
            </div>
        </div>
        </>
    );
};

export default PageHeader;
